import { config } from '@iheartjane/config';
import { statePostalCode } from '@iheartjane/utils';
import { Helmet } from 'react-helmet-async';

import type { Store } from '@jane/shared/types';
import { appendDispensaryToStoreName } from '@jane/shared/util';

interface NestedObj {
  [key: string]: string | NestedObj;
}

export interface PageTitleProps {
  cityState?: string;
  description?: string;
  janeGold?: boolean;
  // keep google from following and indexing the links on this page
  noFollow?: boolean;
  // keep google from indexing this page
  noIndex?: boolean;
  noSuffix?: boolean;
  store?: Store;
  title?: string;
  url?: string;
}

const getStoreCityState = (store?: Store) => {
  return !!store?.city && !!store?.state
    ? `${store?.city}, ${statePostalCode(store?.state)}`
    : undefined;
};

// https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data
const buildStructuredData = (): NestedObj => ({
  '@context': 'https://schema.org/',
  '@type': 'WebSite',
  url: config.deployPublicPath,
  name: 'Jane Technologies',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${config.deployPublicPath}products?searchText={search_term}`,
    },
    'query-input': 'required name=search_term',
  },
});

export const PageTitle = ({
  cityState,
  title,
  janeGold,
  noIndex = false,
  noFollow = false,
  noSuffix = false,
  description,
  store,
  url,
}: PageTitleProps) => {
  const storeCityState = getStoreCityState(store);
  const location = cityState || storeCityState;

  const pageTitleSuffix = store
    ? `${appendDispensaryToStoreName(store.name, ' Dispensary')} ${storeCityState}`
    : janeGold
      ? 'Jane Gold'
      : 'Jane';
  const pageTitle = `${
    title ||
    `Shop cannabis dispensaries and brand deals ${
      location ? `in ${location}` : 'near me'
    }`
  }${noSuffix ? '' : ` | ${pageTitleSuffix}`}`;

  const storeDescription = store
    ? `Visit ${appendDispensaryToStoreName(store.name, ' dispensary')} in ${store.city}, ${store.state} and order ${
        store.recreational && store.medical
          ? 'recreational and medical'
          : store.recreational
            ? 'recreational'
            : store.medical
              ? 'medical'
              : ''
      } products online for ${
        store.pickup && store.delivery
          ? 'pickup and delivery'
          : store.pickup
            ? 'pickup'
            : store.delivery
              ? 'delivery'
              : ''
      }. Browse our online dispensary menu for flower, edibles, vape and more.`
    : undefined;

  const metaDescription =
    description ||
    storeDescription ||
    `Find reviews and explore the best cannabis dispensaries ${
      location ? `in ${location}` : 'near me'
    } with Jane. Browse dispensary menus and order for delivery or pick up.`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta property="og:title" content={pageTitle} />
      <meta property="twitter:title" content={pageTitle} />
      {noIndex && <meta name="robots" content="noindex" />}
      {noFollow && <meta name="robots" content="nofollow" />}

      <meta name="description" content={metaDescription} />
      <meta property="og:description" content={metaDescription} />

      {url && <link rel="canonical" href={url} />}
      {url && <meta property="og:url" content={url} />}

      {!store && (
        <script type="application/ld+json">
          {JSON.stringify(buildStructuredData())}
        </script>
      )}
    </Helmet>
  );
};
